import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/**
 * Global click handler for cart-drawer triggers.
 *
 * The drawer is rendered ONCE at the body level (layout/theme.liquid)
 * so it survives the mobile @media rule that hides the header. Triggers
 * (the desktop header cart icon, the mobile bottom-nav cart link, etc.)
 * use `data-action="open-cart-drawer"` and this delegated handler finds
 * the singleton component and calls its public `.open()` method.
 *
 * Keeps the `href` on link triggers so right-click / middle-click /
 * no-JS users still get to /cart as a graceful fallback.
 */
document.addEventListener('click', (event) => {
  // Honor modifier clicks (open-in-new-tab etc.) — don't hijack those.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (event.button !== undefined && event.button !== 0) return;

  const trigger = event.target.closest('[data-action="open-cart-drawer"]');
  if (!trigger) return;

  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer || typeof drawer.open !== 'function') {
    // No drawer in DOM (e.g. on /cart page) — let the native link/button do its thing.
    return;
  }

  event.preventDefault();
  drawer.open();
});
